import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { isPlatformBrowser } from '@angular/common';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';
/**
 * Spiral Bar Timeline Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-spiral-bar-timeline-graph',
  standalone: false,
  templateUrl: './spiral-bar-timeline-graph.component.html',
  styleUrl: './spiral-bar-timeline-graph.component.scss'
})
export class SpiralBarTimelineGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private spiralBarTimelineGraphReportSubscription: any;
  private isSpiralBarTimelineGraphViewInitialized = false;
  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.spiralBarTimelineGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Spiral Bar Timeline Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isSpiralBarTimelineGraphViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isSpiralBarTimelineGraphViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isSpiralBarTimelineGraphViewInitialized) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  createChart(data: BarChartData[]) {
    this.root = this._baseChartService.createRoot('spiral-bar-timeline-chartdiv');
    this._baseChartService.attachExporting(this.root, { 
      filePrefix: 'spiral-bar-timeline',
      data: data
    });

    const chart = this._baseChartService.createChartContainer(this.root,{
      panX: false,
      panY: false,
      wheelX: 'none',
      wheelY: 'none',
      layout: this.root.verticalLayout
    });
    this._baseChartService.setupColorPalette(chart);

    const categoryField = this._baseChartService.getCategoryField(data);
    const valueFields = this._baseChartService.getValueFields(data);

    // Create axes
    const categoryAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: categoryField,
        renderer: am5xy.AxisRendererX.new(this.root, {})
      })
    );

    const valueAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {})
      })
    );

    // Set data
    categoryAxis.data.setAll(data);

    // Create series for each value field
    valueFields.forEach((valueField, index) => {
      let series = chart.series.push(
        am5xy.ColumnSeries.new(this.root, {
          name: valueField,
          xAxis: categoryAxis,
          yAxis: valueAxis,
          valueYField: valueField,
          categoryXField: categoryField
        })
      );

      // Configure columns with spiral timeline appearance like legacy
      series.columns.template.setAll({
        tooltipText: `{name} in {categoryX}: {valueY}`,
        cornerRadiusTL: 2,
        cornerRadiusTR: 2,
        cornerRadiusBL: 2,
        cornerRadiusBR: 2
      });

      // Set data
      series.data.setAll(data);
    });

    // Add cursor
    chart.set('cursor', am5xy.XYCursor.new(this.root, {
      xAxis: categoryAxis
    }));

    // Add legend
    let legend = chart.children.push(
      this._baseChartService.createLegend(this.root)
    );

    // Configure legend
    legend.set('maxHeight', 40);

    // Configure legend markers
    let markerTemplate = legend.markers.template;
    markerTemplate.set('width', 12);
    markerTemplate.set('height', 12);

    // Set legend data
    legend.data.setAll(chart.series.values);
  }

  private updateChart(data: BarChartData[]) {
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
    }
    this.createChart(data);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.spiralBarTimelineGraphReportSubscription) {
      this.spiralBarTimelineGraphReportSubscription.unsubscribe();
    }
  }
}
